/**
 * Public Meeting Room Booking Page
 * Allows guests to browse and book meeting rooms without login
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Building2, 
  Users, 
  Clock, 
  Calendar,
  DollarSign,
  Wifi,
  Coffee,
  Projector,
  ShoppingCart,
  ArrowLeft,
  CheckCircle,
  MapPin,
  Phone,
  Mail,
  Loader2
} from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';


export default function PublicMeetingRoomBookingPage() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [loading, setLoading] = useState(true);
  const [hotel, setHotel] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);

  const [bookingData, setBookingData] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    company_name: '',
    booking_date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '17:00',
    attendees: 10,
    needs_catering: false,
    needs_projector: false,
    needs_whiteboard: false,
    event_type: 'meeting'
  });

  useEffect(() => {
    loadData();
  }, [hotelId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Fetch hotel details
      const hotelResponse = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}`);
      const hotelData = await hotelResponse.json();
      setHotel(hotelData);

      // Fetch meeting rooms
      const roomsResponse = await fetch(`${BACKEND_URL}/api/meeting-rooms?hotel_id=${hotelId}`);
      const roomsData = await roomsResponse.json();
      
      if (roomsData.success) {
        setRooms(roomsData.rooms || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load meeting rooms');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = (room) => {
    if (!room) return 0;
    
    const startHour = parseInt(bookingData.start_time.split(':')[0]);
    const endHour = parseInt(bookingData.end_time.split(':')[0]);
    const duration = endHour - startHour;

    if (duration >= 8) {
      return room.full_day_rate || room.hourly_rate * 8;
    } else if (duration >= 4) {
      return room.half_day_rate || room.hourly_rate * 4;
    } else {
      return room.hourly_rate * duration;
    }
  };

  const handleBookRoom = (room) => {
    setSelectedRoom(room);
    setShowBookingDialog(true);
  };

  const handleAddToCart = async () => {
    if (!bookingData.guest_name || !bookingData.guest_email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setBookingLoading(true);

    try {
      const startHour = parseInt(bookingData.start_time.split(':')[0]);
      const endHour = parseInt(bookingData.end_time.split(':')[0]);
      const duration = endHour - startHour;

      const cartItemData = {
        hotel_id: hotelId,
        hotel_name: hotel?.name || 'Hotel',
        room_id: selectedRoom.id,
        room_name: selectedRoom.name,
        booking_date: bookingData.booking_date,
        start_time: bookingData.start_time,
        end_time: bookingData.end_time,
        duration_hours: duration,
        attendees: parseInt(bookingData.attendees),
        needs_catering: bookingData.needs_catering,
        needs_projector: bookingData.needs_projector,
        needs_whiteboard: bookingData.needs_whiteboard,
        base_price: calculatePrice(selectedRoom)
      };

      const result = await addToCart('meeting_room', cartItemData);
      
      if (result.success) {
        setShowBookingDialog(false);
        setSelectedRoom(null);
      }
    } catch (error) {
      console.error('Add to cart error:', error);
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="lobby-theme min-h-screen">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/')}
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold heading-font">Meeting Rooms</h1>
                {hotel && (
                  <p className="text-sm text-muted-foreground">{hotel.name}</p>
                )}
              </div>
            </div>
            
            <Button 
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              View Cart
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      {hotel && (
        <section className="border-b bg-gradient-to-br from-primary/5 to-background">
          <div className="container mx-auto px-4 py-12">
            <div className="max-w-3xl">
              <h2 className="text-4xl font-bold heading-font mb-4">
                Professional Meeting Spaces
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Book flexible meeting rooms with modern amenities for your team gatherings, client presentations, or corporate events.
              </p>
              
              {/* Hotel Info */}
              <div className="flex flex-wrap gap-4 text-sm">
                {hotel.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{hotel.location}</span>
                  </div>
                )}
                {hotel.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{hotel.phone}</span>
                  </div>
                )}
                {hotel.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{hotel.email}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Meeting Rooms Grid */}
      <section className="container mx-auto px-4 py-12">
        {rooms.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Building2 className="w-16 h-16 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Meeting Rooms Available</h3>
              <p className="text-sm text-muted-foreground text-center max-w-md">
                This property doesn't have meeting rooms listed yet. Please check back later or contact the hotel directly.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rooms.map((room) => (
              <Card 
                key={room.id} 
                className="overflow-hidden hover:shadow-lg transition-shadow duration-300"
                data-testid={`meeting-room-card-${room.id}`}
              >
                <CardHeader className="bg-primary/5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="heading-font text-xl mb-2">
                        {room.name}
                      </CardTitle>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="w-4 h-4" />
                        <span>Up to {room.capacity} people</span>
                      </div>
                    </div>
                    <Badge variant={room.status === 'available' ? 'default' : 'secondary'}>
                      {room.status}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-6 space-y-4">
                  {/* Amenities */}
                  {(room.amenities && room.amenities.length > 0) && (
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Amenities</h4>
                      <div className="flex flex-wrap gap-2">
                        {room.amenities.includes('wifi') && (
                          <Badge variant="outline" className="text-xs">
                            <Wifi className="w-3 h-3 mr-1" />
                            WiFi
                          </Badge>
                        )}
                        {room.amenities.includes('projector') && (
                          <Badge variant="outline" className="text-xs">
                            <Projector className="w-3 h-3 mr-1" />
                            Projector
                          </Badge>
                        )}
                        {room.amenities.includes('catering') && (
                          <Badge variant="outline" className="text-xs">
                            <Coffee className="w-3 h-3 mr-1" />
                            Catering
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Pricing */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase">Pricing</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Hourly:</span>
                        <span className="font-semibold mono">${room.hourly_rate}/hr</span>
                      </div>
                      {room.half_day_rate && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Half Day:</span>
                          <span className="font-semibold mono">${room.half_day_rate}</span>
                        </div>
                      )}
                      {room.full_day_rate && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Full Day:</span>
                          <span className="font-semibold mono">${room.full_day_rate}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <Button 
                    className="w-full"
                    onClick={() => handleBookRoom(room)}
                    disabled={room.status !== 'available'}
                    data-testid={`book-room-button-${room.id}`}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Book This Room
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-2xl heading-font">
              Book {selectedRoom?.name}
            </DialogTitle>
            <DialogDescription>
              Fill in the details below to reserve this meeting room
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] pr-4">
            <form className="space-y-6">
              {/* Guest Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm uppercase text-muted-foreground">Contact Information</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="guest_name">Full Name *</Label>
                    <Input
                      id="guest_name"
                      value={bookingData.guest_name}
                      onChange={(e) => setBookingData({...bookingData, guest_name: e.target.value})}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="guest_email">Email *</Label>
                    <Input
                      id="guest_email"
                      type="email"
                      value={bookingData.guest_email}
                      onChange={(e) => setBookingData({...bookingData, guest_email: e.target.value})}
                      placeholder="john@company.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="guest_phone">Phone</Label>
                    <Input
                      id="guest_phone"
                      type="tel"
                      value={bookingData.guest_phone}
                      onChange={(e) => setBookingData({...bookingData, guest_phone: e.target.value})}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      value={bookingData.company_name}
                      onChange={(e) => setBookingData({...bookingData, company_name: e.target.value})}
                      placeholder="Acme Corp"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Booking Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm uppercase text-muted-foreground">Booking Details</h3>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="booking_date">Date *</Label>
                    <Input
                      id="booking_date"
                      type="date"
                      value={bookingData.booking_date}
                      onChange={(e) => setBookingData({...bookingData, booking_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="start_time">Start Time *</Label>
                    <Input
                      id="start_time"
                      type="time"
                      value={bookingData.start_time}
                      onChange={(e) => setBookingData({...bookingData, start_time: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="end_time">End Time *</Label>
                    <Input
                      id="end_time"
                      type="time"
                      value={bookingData.end_time}
                      onChange={(e) => setBookingData({...bookingData, end_time: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="attendees">Number of Attendees *</Label>
                    <Input
                      id="attendees"
                      type="number"
                      min="1"
                      max={selectedRoom?.capacity || 100}
                      value={bookingData.attendees}
                      onChange={(e) => setBookingData({...bookingData, attendees: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="event_type">Event Type</Label>
                    <Select 
                      value={bookingData.event_type}
                      onValueChange={(value) => setBookingData({...bookingData, event_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="conference">Conference</SelectItem>
                        <SelectItem value="workshop">Workshop</SelectItem>
                        <SelectItem value="training">Training</SelectItem>
                        <SelectItem value="presentation">Presentation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Additional Services */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm uppercase text-muted-foreground">Additional Services</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="needs_projector"
                      checked={bookingData.needs_projector}
                      onCheckedChange={(checked) => setBookingData({...bookingData, needs_projector: checked})}
                    />
                    <Label htmlFor="needs_projector" className="flex items-center gap-2 cursor-pointer">
                      <Projector className="w-4 h-4" />
                      Projector & Screen
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="needs_catering"
                      checked={bookingData.needs_catering}
                      onCheckedChange={(checked) => setBookingData({...bookingData, needs_catering: checked})}
                    />
                    <Label htmlFor="needs_catering" className="flex items-center gap-2 cursor-pointer">
                      <Coffee className="w-4 h-4" />
                      Catering Service
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="needs_whiteboard"
                      checked={bookingData.needs_whiteboard}
                      onCheckedChange={(checked) => setBookingData({...bookingData, needs_whiteboard: checked})}
                    />
                    <Label htmlFor="needs_whiteboard" className="flex items-center gap-2 cursor-pointer">
                      <Building2 className="w-4 h-4" />
                      Whiteboard
                    </Label>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Price Summary */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Estimated Total:</span>
                  <span className="text-2xl font-bold mono">
                    ${calculatePrice(selectedRoom).toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Final price will be confirmed based on selected services
                </p>
              </div>
            </form>
          </ScrollArea>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowBookingDialog(false)}
              disabled={bookingLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddToCart}
              disabled={bookingLoading}
              data-testid="add-to-cart-button"
            >
              {bookingLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Add to Cart
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
